// CDNA — Custom-styled Candid (Instagram UGC) gallery. Replaces Candid's native
// iframe/Wall widget with our own markup so it matches the LPD design system —
// no candid.js, no jQuery, no Candid CSS loaded at all. Fetches the raw JSON feed
// directly and renders either a uniform grid (homepage) or a horizontally-scrolling
// strip of posts tagged to the current product (PDP).
//
// Candid's real per-product tagging key is the Shopify VARIANT ID (confirmed via
// the legacy widget's own `tag: <variant ids>` call — TagType "Product" / TagId is
// a numeric variant id, e.g. 40880441196617). The homepage feed is a separate,
// manually-curated stream whose Product TagId happens to be a product HANDLE
// string instead. Both are handled below via `data-link-mode`:
//   "tagged"  (homepage) — TagId is a handle; link to /products/<TagId>, else the
//             original Instagram post.
//   "source"  (PDP)      — the feed is already scoped to this product by variant
//             id (via `data-feed-tag`), so every tile is relevant; link out to the
//             original Instagram post rather than back to the page you're already on.
//
// Pagination: Candid's own `&page=N` param (confirmed 2026-07-16 — each page
// returns a genuinely different batch, not a repeat) drives the "Load More"
// button. Candid returns a full page of CDNA_PAGE_SIZE raw entries per `page`
// when more exist, and fewer once you're past the end — that's the only signal
// available to know whether another click is worth showing.
//
// Row discipline (homepage wall): `data-rows` tells JS how many full rows to show
// per batch. `_getCols()` reads the viewport and returns the current column count.
// The initial render and each Load More batch are sliced to exactly rows × cols
// items, so the last row is always complete — no partial row, no dead space.
// Items fetched but not yet shown are held in `this._buffer`; Load More drains
// the buffer before fetching the next Candid page, so no items are skipped.
//
// Silence rule: if the feed is unreachable, or the first page returns nothing,
// the whole section hides itself — same discipline as the rest of the cdna- sections.

var CDNA_PAGE_SIZE = 20;

function cdnaIgTileFromView(view, linkMode) {
  var media = view && view.Media;
  if (!media) return null;

  var images = media.Images || {};
  var src = (images.StandardResolution && images.StandardResolution.Url) ||
            (images.LowResolution && images.LowResolution.Url) ||
            (images.Thumbnail && images.Thumbnail.Url);
  if (!src) return null;
  if (src.indexOf('//') === 0) src = 'https:' + src;

  var productHandle = '';
  if (linkMode === 'tagged') {
    var tagItems = view.StreamEntry && view.StreamEntry.Tags && view.StreamEntry.Tags.Items;
    if (Array.isArray(tagItems)) {
      var productTag = tagItems.find(function (tag) {
        return tag.TagType === 'Product' && tag.TagId;
      });
      if (productTag) productHandle = productTag.TagId;
    }
  }

  var caption = (media.Caption || '').trim();
  var excerpt = caption.length > 90 ? caption.slice(0, 90).trim() + '…' : caption;
  var originalLink = media.OriginalLink || '#';

  return {
    render: function () {
      var isShoppable = Boolean(productHandle);
      var href = isShoppable ? '/products/' + productHandle : originalLink;

      var a = document.createElement('a');
      a.className = 'cdna-ig__tile';
      a.href = href;
      if (!isShoppable) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
      a.setAttribute('aria-label', excerpt || 'View post on Instagram');

      var img = document.createElement('img');
      img.className = 'cdna-ig__img';
      img.src = src;
      img.alt = excerpt || 'Instagram post';
      // CWV hardening: keep these UGC images entirely out of the critical path.
      //  - loading=lazy: never fetched until near the viewport (the strip is below the fold).
      //  - fetchpriority=low: even once fetched, they never contend with the LCP image or
      //    other important resources for bandwidth/connections.
      //  - decoding=async: decode off the main thread so appearing tiles can't cause INP jank.
      // Fixed width/height keep the aspect ratio reserved so the skeleton->tile swap is zero-CLS.
      img.loading = 'lazy';
      img.setAttribute('fetchpriority', 'low');
      img.decoding = 'async';
      img.width = 400;
      img.height = 400;
      img.onerror = function() { a.remove(); };
      a.appendChild(img);

      var overlay = document.createElement('span');
      overlay.className = 'cdna-ig__overlay';
      var cta = document.createElement('span');
      cta.className = isShoppable ? 'cdna-ig__cta cdna-ig__cta--shop' : 'cdna-ig__cta';
      cta.textContent = isShoppable ? 'Shop the Look' : 'View Post';
      overlay.appendChild(cta);
      a.appendChild(overlay);

      return a;
    }
  };
}

var CdnaInstagramGallery = class extends HTMLElement {
  connectedCallback() {
    if (this._observer) return;
    this._buffer = [];
    this._candidPage = 0;
    this._candidRawCount = 0;
    this._loadingMore = false;
    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._observer.disconnect();
          this._load();
        }
      },
      { rootMargin: '400px 0px' }
    );
    this._observer.observe(this);
  }

  // Returns the CSS column count for the current viewport (wall layout only).
  // Must match the breakpoints in cdna-instagram-gallery.css exactly.
  _getCols() {
    if (this.classList.contains('cdna-ig--strip')) return 0;
    var w = window.innerWidth;
    if (w >= 1200) return 6;
    if (w >= 900) return 4;
    if (w >= 550) return 3;
    return 2; // mobile: 2-col vertical grid (strip stays horizontal via CSS class)
  }

  // Items to show per batch: rows × cols when both are set, else data-count fallback.
  _batchSize() {
    var rows = parseInt(this.getAttribute('data-rows'), 10) || 0;
    var cols = this._getCols();
    if (rows > 0 && cols > 0) return rows * cols;
    return parseInt(this.getAttribute('data-count'), 10) || 12;
  }

  async _fetchPage(page) {
    var feedId = this.getAttribute('data-feed-id');
    var feedTag = this.getAttribute('data-feed-tag') || 'homepage';
    var linkMode = this.getAttribute('data-link-mode') || 'tagged';

    // Sending `&page=1` (not just page 2+) breaks results entirely for
    // multi-value (comma-separated variant ID) tags, even though the same
    // param works fine for single-value tags like "homepage" (confirmed
    // 2026-07-16). Omit it on the first page to match the known-working shape;
    // only append it for page 2 onward (Load More).
    var url =
      'https://api.getcandid.com/stream/page/?id=' +
      encodeURIComponent(feedId) +
      '&tag=' +
      encodeURIComponent(feedTag) +
      '&cache=true';
    if (page > 1) {
      url += '&page=' + page;
    }

    var controller = new AbortController();
    var timeout = setTimeout(function () {
      controller.abort();
    }, 8000);

    try {
      var response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeout);
      if (!response.ok) throw new Error('Candid feed request failed: ' + response.status);

      var data = await response.json();
      var views = Array.isArray(data.Views) ? data.Views : [];
      var items = views
        .map(function (view) {
          return cdnaIgTileFromView(view, linkMode);
        })
        .filter(Boolean);

      return { items: items, rawCount: views.length };
    } catch (error) {
      clearTimeout(timeout);
      return null;
    }
  }

  async _load() {
    var feedId = this.getAttribute('data-feed-id');
    var feedTag = this.getAttribute('data-feed-tag') || 'homepage';
    var grid = this.querySelector('.cdna-ig__grid');
    if (!feedId || !feedTag || !grid) return;

    var targetInitial = parseInt(this.getAttribute('data-initial-count'), 10) || 15;

    var result = await this._fetchPage(1);
    if (!result || !result.items.length) {
      this._hide();
      return;
    }

    this._buffer = result.items.slice();
    this._candidPage = 1;
    this._candidRawCount = result.rawCount;

    // Pre-fill the buffer to targetInitial by fetching extra pages while still behind
    // the skeleton (IntersectionObserver already deferred this to 400px before viewport,
    // so these fetches complete before the user sees the section).
    while (this._buffer.length < targetInitial && this._candidRawCount >= CDNA_PAGE_SIZE && this._candidPage < 5) {
      var more = await this._fetchPage(this._candidPage + 1);
      if (!more || !more.items.length) break;
      this._candidPage++;
      this._candidRawCount = more.rawCount;
      this._buffer = this._buffer.concat(more.items);
    }

    // Always render exactly targetInitial items. `data-no-load-more` (theme editor:
    // "Show 'Show more' link" unchecked) only suppresses the button — it does not
    // change how many tiles render, so the count stays predictable either way.
    grid.innerHTML = '';
    this._appendItems(this._buffer.splice(0, targetInitial));
    if (!this.hasAttribute('data-no-load-more')) {
      this._syncLoadMore();
    }

    this.classList.add('cdna-ig--loaded');

    // PDP strip only: the theme's carousel arrows hide themselves by measuring the
    // carousel's scrollability, re-checking whenever its class attribute mutates.
    // Tiles are injected here, long after those buttons connected and measured a
    // grid holding nothing but skeletons, so without a nudge the arrows sit there
    // enabled with nothing to scroll. Touching the grid's class list is exactly what
    // their MutationObserver already watches for.
    //
    // Timing is the whole difficulty here. The tile images load async, so the grid's
    // width keeps changing after append — measuring on the next frame reads geometry
    // that is still settling and leaves the arrows wrongly visible. Re-poke once the
    // images have actually resolved, and again on any later size change.
    this._nudgeCarouselScrollability(grid);
  }

  // Ask the theme's carousel arrows to re-measure whether the rail actually
  // overflows. They hide themselves via a MutationObserver on the carousel's class
  // attribute, so a remove/add of the same class is enough to trigger a re-check
  // without inventing new classes. Only the PDP strip has arrows; on the wall
  // galleries this is a harmless no-op.
  //
  // Called on image load and on resize because the answer genuinely changes: tiles
  // are 150px wide, so whether 12 of them overflow depends entirely on viewport
  // width, and image loading shifts the geometry after the tiles are appended.
  _nudgeCarouselScrollability(grid) {
    var recheck = function () {
      grid.classList.remove('cdna-ig__grid--ready');
      grid.classList.add('cdna-ig__grid--ready');
    };

    recheck();

    var images = grid.querySelectorAll('img');
    Array.prototype.forEach.call(images, function (img) {
      if (img.complete) return;
      img.addEventListener('load', recheck, { once: true });
      img.addEventListener('error', recheck, { once: true });
    });

    if ('ResizeObserver' in window) {
      if (this._railResizeObserver) this._railResizeObserver.disconnect();
      this._railResizeObserver = new ResizeObserver(recheck);
      this._railResizeObserver.observe(grid);
    }
  }

  // Pull the next batch from the local buffer (rows × cols items, or data-count).
  _drain() {
    var n = this._batchSize();
    return this._buffer.splice(0, n);
  }

  _appendItems(items) {
    var grid = this.querySelector('.cdna-ig__grid');
    var fragment = document.createDocumentFragment();
    items.forEach(function (item) {
      fragment.appendChild(item.render());
    });
    grid.appendChild(fragment);
  }

  // Show / hide the Load More button depending on whether more items exist
  // (either buffered locally, or on the next Candid page).
  _syncLoadMore() {
    var hasMore = this._buffer.length > 0 || this._candidRawCount >= CDNA_PAGE_SIZE;
    if (!hasMore) {
      if (this._loadMoreButton) this._loadMoreButton.hidden = true;
      return;
    }

    var label = this.getAttribute('data-load-more-label') || 'Show me more';
    if (!this._loadMoreButton) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cdna-ig__load-more';
      button.textContent = label;
      button.addEventListener('click', () => this._loadMore());
      this.appendChild(button);
      this._loadMoreButton = button;
    }
    this._loadMoreButton.hidden = false;
    this._loadMoreButton.disabled = false;
    this._loadMoreButton.textContent = label;
  }

  async _loadMore() {
    if (!this._loadMoreButton || this._loadingMore) return;
    this._loadingMore = true;
    this._loadMoreButton.disabled = true;
    this._loadMoreButton.textContent = 'Loading…';

    // Drain the local buffer first; only hit the Candid API when it's empty.
    if (this._buffer.length === 0) {
      if (this._candidRawCount < CDNA_PAGE_SIZE) {
        // Last Candid page was short — nothing more to fetch.
        this._loadMoreButton.hidden = true;
        this._loadingMore = false;
        return;
      }
      var result = await this._fetchPage(this._candidPage + 1);
      this._loadingMore = false;
      if (!result || !result.items.length) {
        this._loadMoreButton.hidden = true;
        return;
      }
      this._candidPage += 1;
      this._candidRawCount = result.rawCount;
      this._buffer = result.items.slice();
    } else {
      this._loadingMore = false;
    }

    var batch = this._drain();
    if (!batch.length) {
      this._loadMoreButton.hidden = true;
      return;
    }

    this._appendItems(batch);
    this._syncLoadMore();
  }

  _hide() {
    var section = this.closest('.cdna-ig-section');
    if (section) section.style.display = 'none';
  }
};

if (!window.customElements.get('cdna-instagram-gallery')) {
  window.customElements.define('cdna-instagram-gallery', CdnaInstagramGallery);
}
